import React, { useMemo } from 'react';
import { X, Sun, AlertCircle, Package, Calendar, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { StockAllocation } from '../services/stockAllocationsService';

interface ContractViewModalProps {
  record: any;
  allocations: StockAllocation[] | undefined;
  onClose: () => void;
}

export default function ContractViewModal({ record, allocations, onClose }: ContractViewModalProps) {
  const tech = record.technical_configs?.[0];
  const location = record.installation_locations?.[0];
  const financial = record.financial_terms?.[0];

  const contractPlates = tech?.solar_modules_quantity || 0;
  const contractPanelPower = parseFloat(tech?.solar_modules_power || '0');
  const contractKwp = contractPlates > 0 && contractPanelPower > 0
    ? (contractPlates * contractPanelPower) / 1000
    : 0;

  const allocatedKwp = useMemo(() => {
    if (!allocations || allocations.length === 0) return 0;
    return allocations.reduce((sum, a) => sum + a.quantidade * a.potencia_w, 0) / 1000;
  }, [allocations]);

  const totalAllocatedQty = useMemo(() => {
    if (!allocations || allocations.length === 0) return 0;
    return allocations.reduce((sum, a) => sum + a.quantidade, 0);
  }, [allocations]);

  const hasMismatch = allocations && allocations.length > 0 &&
    (totalAllocatedQty !== contractPlates || Math.abs(allocatedKwp - contractKwp) > 0.01);

  const statusColor = (status: string) => {
    if (status === 'Separada') return 'bg-green-100 text-green-700';
    if (status === 'Reservada') return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Sun className="w-5 h-5 text-blue-600" />
            Visualização do Contrato
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6">
          {/* Dados do cliente */}
          <section>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Cliente</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <span className="text-xs text-gray-400">Nome</span>
                <p className="text-sm font-medium text-gray-900">{record.full_name}</p>
              </div>
              <div>
                <span className="text-xs text-gray-400">CPF</span>
                <p className="text-sm font-medium text-gray-900">{record.cpf}</p>
              </div>
              {record.phone && (
                <div>
                  <span className="text-xs text-gray-400">Telefone</span>
                  <p className="text-sm font-medium text-gray-900">{record.phone}</p>
                </div>
              )}
              {record.email && (
                <div>
                  <span className="text-xs text-gray-400">Email</span>
                  <p className="text-sm font-medium text-gray-900">{record.email}</p>
                </div>
              )}
            </div>
          </section>

          {/* Local de instalação */}
          {location && (
            <section>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Local de Instalação</h3>
              <div className="text-sm text-gray-900">
                <p>{location.street}, {location.number}</p>
                {location.neighborhood && <p>{location.neighborhood}</p>}
                <p>{location.city}{location.state ? ` / ${location.state}` : ''}</p>
              </div>
            </section>
          )}

          {/* Configuração técnica */}
          {tech && (
            <section>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Configuração Técnica</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-gray-400">Marca da placa</span>
                  <p className="text-sm font-medium text-gray-900">{tech.solar_modules_brand || '-'}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Potência por placa</span>
                  <p className="text-sm font-medium text-gray-900">{tech.solar_modules_power ? `${tech.solar_modules_power}W` : '-'}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">Quantidade de placas</span>
                  <p className="text-sm font-medium text-gray-900">{tech.solar_modules_quantity || '-'}</p>
                </div>
                <div>
                  <span className="text-xs text-gray-400">kWp do contrato</span>
                  <p className="text-sm font-medium text-gray-900">{contractKwp > 0 ? `${contractKwp.toFixed(2)} kWp` : '-'}</p>
                </div>
              </div>
            </section>
          )}

          {/* Placas alocadas do estoque */}
          <section className="border-t pt-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Placas Alocadas (do estoque)
            </h3>

            {!allocations || allocations.length === 0 ? (
              <div className="text-sm text-gray-400 italic py-2">
                Nenhuma placa alocada para este contrato.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Modelo</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Marca</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Qtd.</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Potência</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Data</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {allocations.map((a) => (
                        <tr key={a.id}>
                          <td className="px-3 py-2 text-gray-900">{a.modelo}</td>
                          <td className="px-3 py-2 text-gray-900">{a.marca}</td>
                          <td className="px-3 py-2 text-center text-gray-900">{a.quantidade}</td>
                          <td className="px-3 py-2 text-center text-gray-900">{a.potencia_w}W</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(a.status)}`}>
                              {a.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {a.data_alocacao ? format(new Date(a.data_alocacao), 'dd/MM/yyyy') : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Resumo */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Zap className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-gray-900">
                      Total alocado: {totalAllocatedQty} placas = {allocatedKwp.toFixed(2)} kWp
                    </span>
                  </div>
                  {contractPlates > 0 && (
                    <p className="text-xs text-gray-500 ml-6">
                      Contrato prevê {contractPlates} placas ({contractKwp.toFixed(2)} kWp)
                    </p>
                  )}
                </div>

                {/* Aviso de divergência */}
                {hasMismatch && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-800 text-sm">
                        Placas alocadas diferem do contrato
                      </p>
                      <p className="text-xs text-yellow-700 mt-0.5">
                        A quantidade ou potência das placas alocadas no estoque nao coincide com o que está no contrato. Verifique no app de estoque se a alocação está correta.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Termos financeiros */}
          {financial && (
            <section className="border-t pt-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Termos Financeiros</h3>
              <div className="text-sm text-gray-900">
                <p>Valor total: {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(financial.total_amount)}</p>
              </div>
            </section>
          )}

          {/* Data de criação */}
          <section className="border-t pt-5">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Calendar className="w-4 h-4" />
              <span>Criado em {format(new Date(record.created_at), 'dd/MM/yyyy')}</span>
            </div>
          </section>
        </div>

        <div className="px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
